import { useEffect, useRef } from "react";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import { cn } from "@/lib/utils";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingSurface } from "../state/useOnboardingSurface";

/**
 * The heading's id, so `OnboardingModal` can hand its focus trap somewhere
 * worth announcing. Without it the trap grabs the first tabbable element it
 * finds - the header bar's close button - and opening the flow announces
 * "Close setup" instead of the question being asked.
 */
export const ONBOARDING_STEP_HEADING_ID = "onboarding-step-heading";

interface OnboardingStepHeaderProps {
  /** 1-based position in the counted steps; omit where there is no counter. */
  stepNumber?: number | null;
  totalSteps: number;
  title: string;
  subtitle?: string;
  /**
   * "← Back" on the counter row, with the counter pushed right. Only the
   * business claim form draws one (`16224:292764`) — @see `BusinessClaimStep`.
   */
  onBack?: () => void;
  /** Accessible name for the Back link, which says where it goes. */
  backLabel?: string;
  /**
   * Centred on the business branch's "Let's explore!" confirmation, a
   * single-message screen rather than a form heading — as native draws it.
   */
  align?: "left" | "center";
}

/**
 * "Step N of M" + heading (`15984:128872`, "Page title block - RN").
 *
 * Also moves keyboard and screen-reader focus to the heading whenever the step
 * changes, so advancing announces the new question instead of leaving focus on
 * the Continue button that has just been re-rendered under a different label.
 *
 * No back control by default: the flow only goes forward, and an answer is
 * changed by finishing and replaying the flow. The one exception is `onBack`
 * on the business claim form.
 */
const OnboardingStepHeader = ({
  stepNumber,
  totalSteps,
  title,
  subtitle,
  onBack,
  backLabel,
  align = "left",
}: OnboardingStepHeaderProps) => {
  const isCentred = align === "center";
  const i18n = useI18N();
  const { isEngaged } = useOnboardingFlow();
  const { presentation } = useOnboardingSurface();
  const headingRef = useRef<HTMLHeadingElement>(null);

  /*
   * The docked sheet waits until the visitor is using it: it turns up on
   * whatever page they are reading, and yanking focus into a corner of the
   * screen on load would lose their place — @see `onboardingV2EngagedAtom`.
   */
  const shouldFocus = presentation !== "docked" || isEngaged;

  // Keyed on the title: a new question is a new screen as far as anyone
  // listening is concerned.
  useEffect(() => {
    if (shouldFocus) headingRef.current?.focus();
    // Not on `shouldFocus`: becoming engaged is not itself a new screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  return (
    <div className={cn("flex flex-col gap-2", isCentred && "text-center")}>
      <div className="flex flex-col gap-0.5">
        {onBack || stepNumber ? (
          <div className="flex items-center gap-0.5">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={backLabel}
                className="focus-blue label-m-medium flex min-h-6 items-center gap-1.5 rounded-lg text-fg-neutral-strong hover:underline"
              >
                <ArrowLeftIcon size={20} aria-hidden="true" />
                {i18n.get("onboardingBusinessBack")}
              </button>
            ) : null}

            {stepNumber ? (
              <p
                className={cn(
                  "label-s-medium flex-1 text-fg-brand-subtle",
                  onBack && "text-right",
                )}
              >
                {i18n
                  .get("onboardingStepCounter")
                  .replace("{current}", String(stepNumber))
                  .replace("{total}", String(totalSteps))}
              </p>
            ) : null}
          </div>
        ) : null}

        {/*
          `tabIndex={-1}` so focus can be moved here programmatically without
          adding the heading to the tab order. `outline-none` because the focus
          ring on a heading nobody tabbed to reads as a rendering fault — the
          step change is announced, which is the point.
        */}
        <h2
          ref={headingRef}
          id={ONBOARDING_STEP_HEADING_ID}
          tabIndex={-1}
          className="title-l-bold text-fg-neutral-strong outline-none"
        >
          {title}
        </h2>
      </div>

      {subtitle ? (
        <p className="body-m-medium whitespace-pre-line text-fg-neutral-subtle">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
};

export default OnboardingStepHeader;
